import { DASHBOARD_PENDING_EXPENSE_STATUSES } from "@shared/finance/expenseStatusFilters";

export const DASHBOARD_QUEUE_ROLES = ["admin", "project_manager", "admin_manager"] as const;
export type DashboardQueueRole = (typeof DASHBOARD_QUEUE_ROLES)[number];
export type DashboardQueueName = "materials" | "notes";
export type ExpensesTab = "supplier-invoices" | "allowances" | "manual-expenses" | "recurring-expenses";

const EXPENSE_TABS: readonly ExpensesTab[] = [
  "supplier-invoices",
  "allowances",
  "manual-expenses",
  "recurring-expenses",
];

export function canReadDashboardQueues(role: string | undefined): role is DashboardQueueRole {
  return !!role && DASHBOARD_QUEUE_ROLES.includes(role as DashboardQueueRole);
}

export function getDashboardQueuePath(queue: DashboardQueueName): string {
  return `/?queue=${queue}`;
}

export function getDashboardExpensesQueuePath(): string {
  const params = new URLSearchParams({
    tab: "allowances",
    statuses: DASHBOARD_PENDING_EXPENSE_STATUSES.join(","),
  });
  return `/accounting/expenses?${params.toString()}`;
}

function searchParams(search: string): URLSearchParams {
  return new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
}

export function getDashboardQueueFromSearch(search: string): DashboardQueueName | null {
  const queue = searchParams(search).get("queue");
  return queue === "materials" || queue === "notes" ? queue : null;
}

export function getExpensesTabFromSearch(search: string): ExpensesTab {
  const tab = searchParams(search).get("tab");
  return EXPENSE_TABS.includes(tab as ExpensesTab) ? (tab as ExpensesTab) : "supplier-invoices";
}

export function getAllowanceStatusFromSearch(search: string): string {
  const params = searchParams(search);
  const combined = params.get("statuses")?.split(",").filter(Boolean);
  if (
    combined?.length === DASHBOARD_PENDING_EXPENSE_STATUSES.length &&
    DASHBOARD_PENDING_EXPENSE_STATUSES.every((status) => combined.includes(status))
  ) {
    return "pending-review";
  }
  if (combined?.length === 1) return combined[0];
  return params.get("status") || "all";
}

export function getAllowanceStatusQuery(selectedStatus: string): {
  status?: string;
  statuses?: string[];
} {
  if (selectedStatus === "pending-review") {
    return { statuses: [...DASHBOARD_PENDING_EXPENSE_STATUSES] };
  }
  if (selectedStatus === "all") return {};
  return { status: selectedStatus };
}

export function getExpensesTabPath(search: string, tab: ExpensesTab): string {
  const params = searchParams(search);
  params.set("tab", tab);
  return `/accounting/expenses?${params.toString()}`;
}

export function getAllowanceStatusPath(search: string, selectedStatus: string): string {
  const params = searchParams(search);
  params.set("tab", "allowances");
  params.delete("status");
  params.delete("statuses");
  if (selectedStatus === "pending-review") {
    params.set("statuses", DASHBOARD_PENDING_EXPENSE_STATUSES.join(","));
  } else if (selectedStatus !== "all") {
    params.set("status", selectedStatus);
  }
  return `/accounting/expenses?${params.toString()}`;
}

export type QueueBadgeState = "loading" | "error" | "count" | "empty";

export function getQueueBadgeState(input: {
  isLoading: boolean;
  isError: boolean;
  count: number | undefined;
}): QueueBadgeState {
  if (input.isError) return "error";
  if (input.isLoading || input.count === undefined) return "loading";
  return input.count > 0 ? "count" : "empty";
}

export function getQueueAccessibleLabel(
  label: string,
  countUnit: string,
  input: { isLoading: boolean; isError: boolean; count: number | undefined },
): string {
  const state = getQueueBadgeState(input);
  if (state === "error") return `${label}, contador no disponible`;
  if (state === "loading") return `${label}, cargando contador`;
  return `${label}, ${input.count} ${countUnit}`;
}
